import { DocumentNode } from 'graphql/language';
import gql from 'graphql-tag';
import {
  ADDRESS_FRAGMENT_OPERATOR,
  MOBILESERVICE_FRAGMENT_OPERATOR,
  OFFICESERVICE_FRAGMENT_OPERATOR,
  SUBSTITUTE_FRAGMENT_OPERATOR,
  USER_FRAGMENT_OPERATOR,
  SERVICEAREA_FRAGMENT_OPERATOR,
  SERVICE_FRAGMENT_OPERATOR,
  APPLICANT_FRAGMENT_OPERATOR,
  INSTITUTION_FRAGMENT_OPERATOR,
  ESTABLISHMENT_FRAGMENT_OPERATOR,
  DOCTOR_FRAGMENT_OPERATOR,
  DEBTOR_FRAGMENT_OPERATOR,
  BANKACCOUNT_FRAGMENT_OPERATOR,
  LIST_SERVICE_FRAGMENT_OPERATOR,
} from '@/data/gql/operator/fragments';

export const LIST_SERVICES_WITH_LOGISTICS_OPERATOR: DocumentNode = gql`
  query listServicesWithLogisticsPaginated(
    $take: Int
    $skip: Int
    $orderBy: String
    $sortOrder: String
    $assigned: Boolean
    $servicesFrom: DateTime
    $servicesTo: DateTime
    $state: String
    $logisticsStatus: [String]
    $logisticsInformation: ServiceLogisticsInput
    $customerSearchTerm: String
    $substituteSearchTerm: String
  ) {
    listServicesWithLogisticsPaginated(
      take: $take
      skip: $skip
      orderBy: $orderBy
      sortOrder: $sortOrder
      assigned: $assigned
      servicesFrom: $servicesFrom
      servicesTo: $servicesTo
      state: $state
      logisticsStatus: $logisticsStatus
      logisticsInformation: $logisticsInformation
      customerSearchTerm: $customerSearchTerm
      substituteSearchTerm: $substituteSearchTerm
    ) {
      totalCount
      results {
        ... on mobile_service {
          details {
            legacy
            legacy_bonus_fee
            ...listServiceFieldsOperator
            ...serviceLogistics
            ...partnerDetails
            ...linkedServices
          }
        }
        ... on office_service {
          serviceArea {
            name
          }
          details {
            legacy
            legacy_bonus_fee
            ...listServiceFieldsOperator
            ...serviceLogistics
            ...partnerDetails
            ...linkedServices
          }
        }
      }
    }
  }
  ${LIST_SERVICE_FRAGMENT_OPERATOR}
  fragment serviceLogistics on service {
    serviceLogistics {
      id
      logisticsStatus
      partnerRequiresFolderDelivery
      partnerRequiresDoctorsBagDelivery
      partnerRequiresCardReader
      cardReaderSerialNumbers
      substituteHasDoctorsBag
    }
  }
  fragment partnerDetails on service {
    establishmentDoctor {
      debtor {
        debtorNumber
      }
      partner {
        id
        firstName
        lastName
      }
    }
  }
  fragment linkedServices on service {
    parentServiceId
    childServices {
      id
      start
      end
      serviceArea {
        name
        state
      }
    }
  }
`;

export const LIST_SERVICES_OPERATOR: DocumentNode = gql`
  query ListServices(
    $serviceID: UUID
    $start: DateTime
    $end: DateTime
    $minRegularFee: Float
    $minHolidayFee: Float
    $states: [String!]
    $types: [UserServiceType!]
    $assigned: Boolean
    $published: Boolean
    $concluded: Boolean
    $take: Int
    $cursor: UUID
    $orderNumber: String
  ) {
    listServices(
      serviceID: $serviceID
      start: $start
      end: $end
      minRegularFee: $minRegularFee
      minHolidayFee: $minHolidayFee
      states: $states
      types: $types
      assigned: $assigned
      published: $published
      concluded: $concluded
      take: $take
      cursor: $cursor
      orderNumber: $orderNumber
    ) {
      ... on mobile_service {
        details {
          ...listServiceFieldsOperator
        }
      }
      ... on office_service {
        serviceArea {
          name
        }
        details {
          ...listServiceFieldsOperator
        }
      }
    }
  }
  ${LIST_SERVICE_FRAGMENT_OPERATOR}
`;

export const SERVICE_DETAIL_OPERATOR: DocumentNode = gql`
  query ListServices(
    $serviceID: UUID
    $start: DateTime
    $end: DateTime
    $minRegularFee: Float
    $minHolidayFee: Float
    $states: [String!]
    $types: [UserServiceType!]
    $assigned: Boolean
    $published: Boolean
    $concluded: Boolean
    $take: Int
    $cursor: UUID
  ) {
    listServices(
      serviceID: $serviceID
      start: $start
      end: $end
      minRegularFee: $minRegularFee
      minHolidayFee: $minHolidayFee
      states: $states
      types: $types
      assigned: $assigned
      published: $published
      concluded: $concluded
      take: $take
      cursor: $cursor
    ) {
      ... on mobile_service {
        serviceArea {
          ...mobileserviceFieldsOperator
        }
        details {
          ...serviceFieldsOperator
        }
      }
      ... on office_service {
        serviceArea {
          ...officeserviceFieldsOperator
        }
        details {
          ...serviceFieldsOperator
        }
      }
    }
  }
  ${MOBILESERVICE_FRAGMENT_OPERATOR}
  ${OFFICESERVICE_FRAGMENT_OPERATOR}
  ${SERVICE_FRAGMENT_OPERATOR}
  ${APPLICANT_FRAGMENT_OPERATOR}
  ${ADDRESS_FRAGMENT_OPERATOR}
  ${USER_FRAGMENT_OPERATOR}
  ${SERVICEAREA_FRAGMENT_OPERATOR}
  ${DOCTOR_FRAGMENT_OPERATOR}
  ${DEBTOR_FRAGMENT_OPERATOR}
  ${BANKACCOUNT_FRAGMENT_OPERATOR}
`;

export const SERVICE_AREAS_OPERATOR: DocumentNode = gql`
  query QueryServiceAreas($state: String) {
    listServiceAreas(state: $state) {
      id
      name
      validFrom
      validTo
      state
      mobileServices {
        ...mobileserviceFieldsOperator
      }
      officeServices {
        ...officeserviceFieldsOperator
      }
    }
  }
  ${MOBILESERVICE_FRAGMENT_OPERATOR}
  ${OFFICESERVICE_FRAGMENT_OPERATOR}
  ${ADDRESS_FRAGMENT_OPERATOR}
`;

export const LIST_CUSTOMERS_OPERATOR: DocumentNode = gql`
  query QueryCustomers(
    $id: UUID
    $firstName: String
    $lastName: String
    $debtorNumber: Int
    $legacy: Boolean
    $roles: [String!]
    $trainingConfirmed: Boolean
    $verified: Boolean
    $take: Int
    $cursor: UUID
  ) {
    listCustomers(
      userID: $id
      firstName: $firstName
      lastName: $lastName
      debtorNumber: $debtorNumber
      legacy: $legacy
      roles: $roles
      trainingConfirmed: $trainingConfirmed
      verified: $verified
      take: $take
      cursor: $cursor
    ) {
      id
      email
      firstName
      lastName
      mobile1
      termsAcceptedOn
      roles
      legacy
      institutions {
        verified
        debtor {
          debtorNumber
        }
      }
      substitute {
        verified
        debtor {
          debtorNumber
        }
      }
    }
  }
`;

export const CUSTOMERS_OPERATOR: DocumentNode = gql`
  query QueryCustomers(
    $id: UUID
    $firstName: String
    $lastName: String
    $debtorNumber: Int
    $legacy: Boolean
    $roles: [String!]
    $trainingConfirmed: Boolean
    $verified: Boolean
    $take: Int
    $cursor: UUID
  ) {
    listCustomers(
      userID: $id
      firstName: $firstName
      lastName: $lastName
      debtorNumber: $debtorNumber
      legacy: $legacy
      roles: $roles
      trainingConfirmed: $trainingConfirmed
      verified: $verified
      take: $take
      cursor: $cursor
    ) {
      ...userFieldsOperator
      roles
      authorization {
        queries
        mutations
      }
      institutions {
        ...institutionFieldsOperator
      }
      substitute {
        ...substituteFieldsOperator
      }
    }
  }
  ${ADDRESS_FRAGMENT_OPERATOR}
  ${USER_FRAGMENT_OPERATOR}
  ${INSTITUTION_FRAGMENT_OPERATOR}
  ${ESTABLISHMENT_FRAGMENT_OPERATOR}
  ${DOCTOR_FRAGMENT_OPERATOR}
  ${SERVICEAREA_FRAGMENT_OPERATOR}
  ${DEBTOR_FRAGMENT_OPERATOR}
  ${SERVICE_FRAGMENT_OPERATOR}
  ${SUBSTITUTE_FRAGMENT_OPERATOR}
  ${BANKACCOUNT_FRAGMENT_OPERATOR}
  ${APPLICANT_FRAGMENT_OPERATOR}
  ${MOBILESERVICE_FRAGMENT_OPERATOR}
  ${OFFICESERVICE_FRAGMENT_OPERATOR}
`;

export const LOAD_PARTNER_COMPLETE_OPERATOR: DocumentNode = gql`
  query QueryCustomers(
    $id: UUID
    $firstName: String
    $lastName: String
    $debtorNumber: Int
    $legacy: Boolean
    $roles: [String!]
    $trainingConfirmed: Boolean
    $verified: Boolean
    $take: Int
    $cursor: UUID
  ) {
    listCustomers(
      userID: $id
      firstName: $firstName
      lastName: $lastName
      debtorNumber: $debtorNumber
      legacy: $legacy
      roles: $roles
      trainingConfirmed: $trainingConfirmed
      verified: $verified
      take: $take
      cursor: $cursor
    ) {
      ...userFieldsOperator
      roles
      substitute {
        id
      }
      institutions {
        ...institutionFieldsOperator
      }
    }
  }
  ${ADDRESS_FRAGMENT_OPERATOR}
  ${USER_FRAGMENT_OPERATOR}
  ${INSTITUTION_FRAGMENT_OPERATOR}
  ${ESTABLISHMENT_FRAGMENT_OPERATOR}
  ${DOCTOR_FRAGMENT_OPERATOR}
  ${SERVICEAREA_FRAGMENT_OPERATOR}
  ${DEBTOR_FRAGMENT_OPERATOR}
  ${SERVICE_FRAGMENT_OPERATOR}
  ${BANKACCOUNT_FRAGMENT_OPERATOR}
  ${APPLICANT_FRAGMENT_OPERATOR}
  ${MOBILESERVICE_FRAGMENT_OPERATOR}
  ${OFFICESERVICE_FRAGMENT_OPERATOR}
`;

export const LOAD_PARTNER_NAMES_OPERATOR: DocumentNode = gql`
  query QueryCustomers($id: UUID, $firstName: String, $lastName: String, $legacy: Boolean, $roles: [String!], $take: Int, $cursor: UUID) {
    listCustomers(userID: $id, firstName: $firstName, lastName: $lastName, legacy: $legacy, roles: $roles, take: $take, cursor: $cursor) {
      id
      title
      firstName
      lastName
      legacy
      roles
    }
  }
`;

export const LOAD_SUBSTITUTE_OPERATOR: DocumentNode = gql`
  query QueryCustomers(
    $id: UUID
    $firstName: String
    $lastName: String
    $debtorNumber: Int
    $legacy: Boolean
    $roles: [String!]
    $trainingConfirmed: Boolean
    $verified: Boolean
    $take: Int
    $cursor: UUID
  ) {
    listCustomers(
      userID: $id
      firstName: $firstName
      lastName: $lastName
      debtorNumber: $debtorNumber
      legacy: $legacy
      roles: $roles
      trainingConfirmed: $trainingConfirmed
      verified: $verified
      take: $take
      cursor: $cursor
    ) {
      ...userFieldsOperator
      roles
      substitute {
        ...substituteFieldsOperator
      }
      institutions {
        id
      }
    }
  }
  ${ADDRESS_FRAGMENT_OPERATOR}
  ${USER_FRAGMENT_OPERATOR}
  ${SERVICEAREA_FRAGMENT_OPERATOR}
  ${DEBTOR_FRAGMENT_OPERATOR}
  ${SERVICE_FRAGMENT_OPERATOR}
  ${DOCTOR_FRAGMENT_OPERATOR}
  ${SUBSTITUTE_FRAGMENT_OPERATOR}
  ${BANKACCOUNT_FRAGMENT_OPERATOR}
  ${APPLICANT_FRAGMENT_OPERATOR}
  ${MOBILESERVICE_FRAGMENT_OPERATOR}
  ${OFFICESERVICE_FRAGMENT_OPERATOR}
`;

export const CUSTOMERS_KORRESPONDENZ: DocumentNode = gql`
  query CustomersKorrespondenz($id: UUID) {
    listCustomers(userID: $id, roles: ["substitute"]) {
      title
      firstName
      lastName
      legacy
      id
      substitute {
        debtor {
          debtorNumber
        }
        liabilityInsurances {
          id
          validFrom
          validTo
          insuranceName
          insuranceNumber
          document {
            url
            uploadedOn
          }
        }
      }
    }
  }
`;

export const CUSTOMERS_SPECIALIZATIONS: DocumentNode = gql`
  query CustomersSPECIALIZATIONS($id: UUID) {
    listCustomers(userID: $id, roles: ["substitute"]) {
      id
      substitute {
        specializations {
          id
          certificate {
            url
            uploadedOn
          }
        }
      }
    }
  }
`;

export const CUSTOMERS_LICENSE: DocumentNode = gql`
  query CustomersKorrespondenz($id: UUID) {
    listCustomers(userID: $id, roles: ["substitute"]) {
      id
      substitute {
        license {
          url
          uploadedOn
        }
      }
    }
  }
`;

export const CUSTOMER_SERVICE_SUBMISSIONS: DocumentNode = gql`
  query CustomersServiceSubmissions($id: UUID, $take: Int, $cursor: UUID, $legacy: Boolean) {
    listCustomers(userID: $id, take: $take, cursor: $cursor, legacy: $legacy, roles: ["partner"]) {
      id
      title
      firstName
      lastName
      phone
      email
      institutions {
        name
        establishments {
          id
          name
          billingAddress {
            state
          }
          submissions {
            id
            establishment {
              id
            }
            content
            service {
              id
            }
          }
        }
      }
    }
  }
`;

export const SEARCH_USERS_BY_NAME: DocumentNode = gql`
  query SearchUsersByName($searchterm: String, $role: String) {
    users(
      where: {
        AND: [
          { user_role: { some: { role: { equals: $role } } } }
          { OR: [{ first_name: { contains: $searchterm } }, { last_name: { contains: $searchterm } }] }
        ]
      }
      orderBy: [{ registration_date: desc }]
    ) {
      firstName
      lastName
      registrationDate
      id
      email
      substitute {
        id
        debtor {
          debtorNumber
        }
      }
    }
  }
`;

export const GET_USERS_BY_ROLE: DocumentNode = gql`
  query getAllUsers($role: String, $take: Int) {
    users(where: { user_role: { some: { role: { equals: $role } } } }, first: $take, orderBy: [{ registration_date: desc }, { email: asc }]) {
      title
      firstName
      lastName
      birthDate
      phone
      mobile1
      mobile2
      fax
      legacy
      registrationDate
      id
      email
      institutions {
        bsnr
        name
        address {
          city
          street
          country
          houseNr
          zipCode
          state
        }
        type
        kvSeats
        debtor {
          debtorNumber
          billingAddress {
            city
            street
            country
            houseNr
            zipCode
            state
          }
          tariff {
            period
            paymentPeriod
            payment_method {
              ... on debtor_bill_payment_method {
                __typename
                bankAccount {
                  accountHolder
                  bank
                  iban
                  bic
                }
              }
              ... on debtor_sepa_payment_method {
                __typename
                bankAccount {
                  accountHolder
                  bank
                  iban
                  bic
                }
              }
            }
          }
        }
      }
      substitute {
        taxNumber
        trainingConfirmed
        verified
        bankAccount {
          accountHolder
          bank
          iban
          bic
        }
        address {
          city
          street
          country
          houseNr
          zipCode
          state
        }
        debtor {
          debtorNumber
        }
      }
    }
  }
`;

export const GET_USERS_BY_ROLE_CURSOR: DocumentNode = gql`
  query getAllUsers($role: String, $take: Int, $cursor: String) {
    users(
      where: { user_role: { some: { role: { equals: $role } } } }
      first: $take
      after: { id: $cursor }
      orderBy: [{ registration_date: desc }, { email: asc }]
    ) {
      title
      firstName
      lastName
      birthDate
      phone
      mobile1
      mobile2
      fax
      legacy
      registrationDate
      id
      email
      institutions {
        bsnr
        name
        address {
          city
          street
          country
          houseNr
          zipCode
          state
        }
        type
        kvSeats
        debtor {
          debtorNumber
          billingAddress {
            city
            street
            country
            houseNr
            zipCode
            state
          }
          tariff {
            period
            paymentPeriod
            payment_method {
              ... on debtor_bill_payment_method {
                __typename
                bankAccount {
                  accountHolder
                  bank
                  iban
                  bic
                }
              }
              ... on debtor_sepa_payment_method {
                __typename
                bankAccount {
                  accountHolder
                  bank
                  iban
                  bic
                }
              }
            }
          }
        }
      }
      substitute {
        taxNumber
        trainingConfirmed
        verified
        bankAccount {
          accountHolder
          bank
          iban
          bic
        }
        address {
          city
          street
          country
          houseNr
          zipCode
          state
        }
        debtor {
          debtorNumber
        }
      }
    }
  }
`;

export const GET_USERS_BY_ROLE_LIMIT: DocumentNode = gql`
  query getAllUsers($role: String, $take: Int) {
    users(where: { user_role: { some: { role: { equals: $role } } } }, first: $take, orderBy: [{ registration_date: desc }, { email: asc }]) {
      title
      firstName
      lastName
      birthDate
      phone
      mobile1
      mobile2
      fax
      legacy
      registrationDate
      id
      email
      institutions {
        bsnr
        name
        address {
          city
          street
          country
          houseNr
          zipCode
          state
        }
        type
        kvSeats
        debtor {
          debtorNumber
          billingAddress {
            city
            street
            country
            houseNr
            zipCode
            state
          }
          tariff {
            period
            paymentPeriod
            payment_method {
              ... on debtor_bill_payment_method {
                __typename
                bankAccount {
                  accountHolder
                  bank
                  iban
                  bic
                }
              }
              ... on debtor_sepa_payment_method {
                __typename
                bankAccount {
                  accountHolder
                  bank
                  iban
                  bic
                }
              }
            }
          }
        }
      }
      substitute {
        taxNumber
        trainingConfirmed
        verified
        bankAccount {
          accountHolder
          bank
          iban
          bic
        }
        address {
          city
          street
          country
          houseNr
          zipCode
          state
        }
        debtor {
          debtorNumber
        }
      }
    }
  }
`;

export const GET_UNPUBLISHED_MOBILESERVICES_OPERATOR: DocumentNode = gql`
  query {
    mobileServices(where: { service: { NOT: { published_service: { published_on: { gte: "1970-01-01T00:00:00.000Z" } } } } }) {
      serviceArea {
        name
      }
      details {
        id
        start
        end
        published
        concluded
        kind
        establishment {
          name
          bsnr
          serviceArea {
            name
          }
          ownedBy {
            debtor {
              debtorNumber
            }
          }
        }
        establishmentDoctor {
          name
          lanr
        }
        substitute {
          details {
            user {
              title
              firstName
              lastName
            }
            debtor {
              debtorNumber
            }
          }
        }
      }
    }
  }
`;

export const GET_UNPUBLISHED_OFFICESERVICES_OPERATOR: DocumentNode = gql`
  query {
    officeServices(where: { service: { NOT: { published_service: { published_on: { gte: "1970-01-01T00:00:00.000Z" } } } } }) {
      serviceArea {
        name
      }
      details {
        id
        start
        end
        published
        concluded
        kind
        establishment {
          name
          bsnr
          serviceArea {
            name
          }
          ownedBy {
            debtor {
              debtorNumber
            }
          }
        }
        establishmentDoctor {
          name
          lanr
        }
        substitute {
          details {
            user {
              title
              firstName
              lastName
            }
            debtor {
              debtorNumber
            }
          }
        }
      }
    }
  }
`;

export const GET_MOBILESERVICES_OPERATOR: DocumentNode = gql`
  query {
    mobileServices {
      serviceArea {
        name
      }
      details {
        id
        start
        end
        published
        concluded
        kind
        establishment {
          name
          bsnr
          serviceArea {
            name
          }
          ownedBy {
            debtor {
              debtorNumber
            }
          }
        }
        establishmentDoctor {
          name
          lanr
        }
        applicants {
          details {
            debtor {
              debtorNumber
            }
            user {
              title
              firstName
              lastName
            }
          }
        }
        substitute {
          details {
            user {
              title
              firstName
              lastName
            }
            debtor {
              debtorNumber
            }
          }
        }
      }
    }
  }
`;

export const GET_OFFICESERVICES_OPERATOR: DocumentNode = gql`
  query {
    officeServices {
      serviceArea {
        name
      }
      details {
        id
        start
        end
        published
        concluded
        kind
        establishment {
          name
          bsnr
          serviceArea {
            name
          }
          ownedBy {
            debtor {
              debtorNumber
            }
          }
        }
        establishmentDoctor {
          name
          lanr
        }
        applicants {
          details {
            debtor {
              debtorNumber
            }
            user {
              title
              firstName
              lastName
            }
          }
        }
        substitute {
          details {
            user {
              title
              firstName
              lastName
            }
            debtor {
              debtorNumber
            }
          }
        }
      }
    }
  }
`;

export const GET_ENDED_MOBILESERVICES_OPERATOR: DocumentNode = gql`
  query getEndedMobileServices($end: DateTime) {
    mobileServices(where: { service: { end: { lte: $end } } }) {
      serviceArea {
        name
      }
      details {
        id
        start
        end
        published
        concluded
        kind
        establishment {
          name
          bsnr
          serviceArea {
            name
          }
          ownedBy {
            debtor {
              debtorNumber
            }
          }
        }
        establishmentDoctor {
          name
          lanr
        }
        substitute {
          details {
            user {
              title
              firstName
              lastName
            }
            debtor {
              debtorNumber
            }
          }
        }
      }
    }
  }
`;

export const GET_ENDED_OFFICESERVICES_OPERATOR: DocumentNode = gql`
  query getEndedOfficeServices($end: DateTime) {
    officeServices(where: { service: { end: { lte: $end } } }) {
      serviceArea {
        name
      }
      details {
        id
        start
        end
        published
        concluded
        kind
        establishment {
          name
          bsnr
          serviceArea {
            name
          }
          ownedBy {
            debtor {
              debtorNumber
            }
          }
        }
        establishmentDoctor {
          name
          lanr
        }
        substitute {
          details {
            user {
              title
              firstName
              lastName
            }
            debtor {
              debtorNumber
            }
          }
        }
      }
    }
  }
`;

export const GET_USERS_BY_ID_OPERATOR: DocumentNode = gql`
  query getAllUsers($ids: [String!]) {
    users(where: { id: { in: $ids } }) {
      id
      title
      firstName
      lastName
      email
    }
  }
`;
